import { CollectionProductResponse } from 'api/product';
import { PdProductItem } from 'interfaces/line-item';
import { CollectionProduct } from 'models/collection/collection';
import { StaticPdp } from 'modules/product-details/types';

interface IsProductIdInCartArgs {
  id: string;
  cartItems: PdProductItem[];
}

/**
 * Checks if an id is present in the cart items
 */
export function isProductIdInCart({ id, cartItems }: IsProductIdInCartArgs): boolean {
  return !!cartItems.find((cartItem) => String(cartItem.productId) === id);
}

interface CreateEnhancedProductOptions {
  imagePlaceholder?: string;
}

/**
 * Creates an enhanced CollectionProduct with all variants from StaticPdp data.
 * Used when a product from smart-collection (which only has 1 variant) needs full variant data.
 */
export const createEnhancedProduct = (
  originalProduct: CollectionProduct,
  productDetails: StaticPdp,
  options: CreateEnhancedProductOptions = {}
): CollectionProduct => {
  const { imagePlaceholder = '' } = options;
  const transformedProduct: Omit<
    CollectionProductResponse,
    '_updatedAt' | '_updated' | 'sk' | 'pk'
  > = {
    id: parseInt(productDetails.product.id),
    productId: productDetails.product.id,
    productType: productDetails.product.productType,
    title: originalProduct.title,
    tags: productDetails.product.tags,
    handle: productDetails.product.handle,
    availableForSale: productDetails.product.availableForSale,
    image: {
      src: productDetails.product.images[0]?.originalSrc || '',
      alt: productDetails.product.images[0]?.altText || imagePlaceholder || undefined,
    },
    createdAt: '',
    metadata: {
      accentuate: {
        ribbonText1: '',
        preorderText: '',
        hideCollections: [],
        subTitle: '',
        ribbonColor: '',
        mainTitle: originalProduct.title,
      },
      subscription: {
        intervals: '',
        enabled: false,
        variants: [],
      },
      rating: {
        count: '',
        score: '',
      },
      petsdeli: {
        uid: '',
        salesCounter: 0,
      },
    },
    variants: productDetails.product.variants.map((v) => ({
      id: parseInt(v.id),
      title: v.title,
      sku: v.sku,
      weight: v.weight,
      weightUnit: v.weightUnit,
      priceV2: v.priceV2,
      availableForSale: v.availableForSale,
      compareAtPrice: v.compareAtPrice ? parseFloat(v.compareAtPrice) * 100 : 0,
      image: {
        src: v.image?.originalSrc || '',
        alt: v.image?.altText || imagePlaceholder || undefined,
      },
    })),
  };

  return new CollectionProduct(transformedProduct);
};
